import React, { useState, useRef } from 'react';
import { API_BASE } from '../../config';
import { useDomainValidation } from '../../hooks/useDomainValidation';
import DomainValidationModal from '../DomainValidationRetryModal';
import { handleAIError } from '../../utils/aiErrorHandler';
import CostChip from '../CostChip';
import { useCreditSpend } from '../../contexts/CreditSpendContext';

import { useEscapeToClose } from '../../hooks/useEscapeToClose';
function RecipeForm({ onClose, onSave, fetchWithAuth, editingRecipe, onOpenRecipeGenerator }) {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const { spend } = useCreditSpend();  // #77 recipe_image = 4 credits → always confirms
  // EDIT MODE: simplified modal (name, description, date, active/inactive, generate image)
  // ADD MODE: mode selector → manual entry with optional AI extraction
  const isEdit = !!editingRecipe;

  const [formData, setFormData] = useState({
    recipe_name: editingRecipe?.title || '',
    description: editingRecipe?.description || '',
    recipe_status: editingRecipe?.recipe_status || 'active_as_is',
    recipe_date: editingRecipe?.recipe_date || '',
    ingredients: '',
    instructions: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
  });

  // Add mode states
  const [recipeMode, setRecipeMode] = useState('select'); // 'select' | 'manual'
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionDone, setExtractionDone] = useState(false);
  const [extractionError, setExtractionError] = useState(null);
  const [useAsImage, setUseAsImage] = useState(true);
  const [generateImage, setGenerateImage] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageGenResult, setImageGenResult] = useState(null);
  const { domainValidation, handleValidationResponse, clearValidation } = useDomainValidation();
  const fileInputRef = useRef(null);

  // File validation
  const validateFile = (file) => {
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'text/plain'];
    const maxSize = 10 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      alert('File type not allowed. Please use PDF, images (JPG, PNG), or text files.');
      return false;
    }
    if (file.size > maxSize) {
      alert('File too large. Maximum size is 10MB.');
      return false;
    }
    return true;
  };

  const handleFileSelect = (file) => {
    if (validateFile(file)) {
      setSelectedFile(file);
      setExtractionDone(false);
      setExtractionError(null);
      // Default useAsImage on for image files
      setUseAsImage(file.type.startsWith('image/'));
      // Default generateImage off for images (user has the photo), on for non-images
      setGenerateImage(!file.type.startsWith('image/'));
    }
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) handleFileSelect(e.dataTransfer.files[0]);
  };
  const handleFileInputChange = (e) => {
    if (e.target.files.length > 0) handleFileSelect(e.target.files[0]);
  };

  const removeFile = () => {
    setSelectedFile(null);
    setExtractionDone(false);
    setExtractionError(null);
    setUseAsImage(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // The server's `detail` is a string for most errors but an object ({ message, ... }) for the credit
  // gate; rendering the object would crash React. Always reduce it to text.
  const detailText = (d) => (typeof d === 'string' ? d : (d && d.message) || '');

  // A photo is read by the multimodal model, a PDF / text file by the text model — two prices.
  const extractApiType = () =>
    (selectedFile && selectedFile.type.startsWith('image/') ? 'recipe_from_image' : 'recipe_text_extract');

  // AI Extraction from uploaded file
  const handleExtract = async () => {
    if (!selectedFile) return;
    setIsExtracting(true);
    setExtractionError(null);

    try {
      const extractFormData = new FormData();
      extractFormData.append('file', selectedFile);

      const response = await fetch(`${API_BASE}/api/recipes/extract-from-file`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: extractFormData
      });

      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          recipe_name: data.recipe_name || prev.recipe_name,
          description: data.description || prev.description,
          ingredients: data.ingredients || '',
          instructions: data.instructions || '',
          calories: data.calories != null ? String(data.calories) : '',
          protein: data.protein != null ? String(data.protein) : '',
          carbs: data.carbs != null ? String(data.carbs) : '',
          fat: data.fat != null ? String(data.fat) : '',
        }));
        setExtractionDone(true);
      } else if (await handleAIError(response)) {
        // Out of credits / not on this plan — the unified AI limit modal is shown (B-CREDIT1 fix 2).
      } else if (response.status === 422) {
        const err = await response.json().catch(() => ({}));
        setExtractionError(detailText(err.detail) || 'The uploaded file does not contain a valid recipe.');
      } else if (response.status === 503) {
        setExtractionError('AI extraction service temporarily unavailable. Please try again or enter the recipe manually.');
      } else {
        const err = await response.json().catch(() => ({}));
        setExtractionError(detailText(err.detail) || 'Failed to extract recipe from file.');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      setExtractionError(`Error: ${error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };

  // Generate AI image for recipe (Edit mode)
  const handleGenerateImage = async () => {
    if (!editingRecipe?.id) return;
    setGeneratingImage(true);
    setImageGenResult(null);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/recipes/${editingRecipe.id}/generate-image`, {
        method: 'POST',
      });
      if (response.ok) {
        const data = await response.json();
        setImageGenResult({ success: true, image_url: data.image_url });
      } else if (await handleAIError(response)) {
        // unified AI limit modal shown — no inline error state
      } else {
        setImageGenResult({ success: false, error: 'Failed to generate image' });
      }
    } catch (error) {
      setImageGenResult({ success: false, error: error.message });
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate: need recipe_name + at least some content
    if (!formData.recipe_name.trim()) {
      alert('Please enter a recipe name');
      return;
    }
    if (!isEdit && !selectedFile && !formData.ingredients.trim() && !formData.instructions.trim()) {
      alert('Please provide ingredients or instructions, or upload a file');
      return;
    }

    setIsSaving(true);

    try {
      let response;
      if (isEdit) {
        response = await fetchWithAuth(`${API_BASE}/api/recipes/${editingRecipe.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: formData.recipe_name,
            description: formData.description,
            recipe_status: formData.recipe_status,
            recipe_date: formData.recipe_date,
          })
        });
      } else {
        const formDataToSend = new FormData();
        if (selectedFile) {
          formDataToSend.append('file', selectedFile);
        }
        formDataToSend.append('recipe_name', formData.recipe_name);
        formDataToSend.append('description', formData.description || '');
        formDataToSend.append('recipe_status', 'active_as_is');
        formDataToSend.append('recipe_date', formData.recipe_date || '');
        if (formData.ingredients.trim()) {
          formDataToSend.append('ingredients', formData.ingredients);
        }
        if (formData.instructions.trim()) {
          formDataToSend.append('instructions', formData.instructions);
        }
        // Build recipe_text from ingredients + instructions for the recipe_text column
        const recipeTextParts = [];
        if (formData.ingredients.trim()) recipeTextParts.push(`Ingredients:\n${formData.ingredients}`);
        if (formData.instructions.trim()) recipeTextParts.push(`\nInstructions:\n${formData.instructions}`);
        if (recipeTextParts.length > 0) {
          formDataToSend.append('recipe_text', recipeTextParts.join('\n'));
        }
        if (formData.calories) formDataToSend.append('calories', formData.calories);
        if (formData.protein) formDataToSend.append('protein', formData.protein);
        if (formData.carbs) formDataToSend.append('carbs', formData.carbs);
        if (formData.fat) formDataToSend.append('fat', formData.fat);
        formDataToSend.append('use_as_recipe_image', useAsImage && selectedFile?.type?.startsWith('image/') ? 'true' : 'false');

        response = await fetch(`${API_BASE}/api/recipes/upload-with-recipe`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          body: formDataToSend
        });
      }

      const validationResult = await handleValidationResponse(response, 'recipe');
      if (validationResult === true) {
        onSave();
        onClose();
      } else if (validationResult === null) {
        const error = await response.text();
        alert(`Failed to save recipe: ${error}`);
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Domain validation modal (shared across all RecipeForm returns)
  const domainValidationModal = domainValidation && (
    <DomainValidationModal
      isOpen={true}
      mode={domainValidation.mode}
      rejectionMessage={domainValidation.message}
      entityType={domainValidation.entityType}
      onClose={clearValidation}
    />
  );

  // ========== EDIT MODE: Simplified Modal ==========
  if (isEdit) {
    return (
      <>{domainValidationModal}<div className="modal-overlay">
        <div className="modal-content modal-content-md">
          <div className="modal-header">
            <h3 className="modal-title">Edit Recipe</h3>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Recipe Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.recipe_name}
                onChange={(e) => setFormData(prev => ({ ...prev, recipe_name: e.target.value }))}
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                className="form-textarea"
                rows="4"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Date</label>
              <input
                type="date"
                className="form-input"
                value={formData.recipe_date}
                onChange={(e) => setFormData(prev => ({ ...prev, recipe_date: e.target.value }))}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Status</label>
              <div className="rf-statusToggle">
                <button
                  type="button"
                  className={`rf-statusBtn ${formData.recipe_status !== 'inactive' ? 'rf-statusBtn--active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, recipe_status: 'active_as_is' }))}
                >
                  Active
                </button>
                <button
                  type="button"
                  className={`rf-statusBtn ${formData.recipe_status === 'inactive' ? 'rf-statusBtn--inactive' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, recipe_status: 'inactive' }))}
                >
                  Inactive
                </button>
              </div>
            </div>

            <hr />

            <div className="form-group">
              <label className="form-label">AI Image Generation</label>
              <p className="rf-imageHint">Generate an AI image of this recipe using gpt-image-1</p>
              {editingRecipe.image_url && (
                <div className="rf-currentImage">
                  <img src={editingRecipe.image_url.startsWith('/') ? `${API_BASE}${editingRecipe.image_url}` : editingRecipe.image_url} alt="Recipe" className="rf-imagePreview" />
                </div>
              )}
              {imageGenResult?.success && (
                <div className="rf-currentImage">
                  <img src={imageGenResult.image_url.startsWith('/') ? `${API_BASE}${imageGenResult.image_url}` : imageGenResult.image_url} alt="Generated" className="rf-imagePreview" />
                  <p className="rf-imageSuccess">Image generated successfully</p>
                </div>
              )}
              {imageGenResult && !imageGenResult.success && (
                <p className="rf-imageError">{imageGenResult.error}</p>
              )}
              {generatingImage && (
                <div className="rf-imageLoading">
                  <div className="ainc-spinner"></div>
                  <p className="ainc-spinner-text">Generating image...</p>
                </div>
              )}
              {!editingRecipe.image_url && !imageGenResult?.success && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => spend('recipe_image', 'Generate a recipe image?', handleGenerateImage)}
                  disabled={generatingImage}
                >
                  {generatingImage ? 'Generating Image...' : <>Generate Image <CostChip apiType="recipe_image" /></>}
                </button>
              )}
            </div>

            <hr />

            <div className="form-actions">
              <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSaving}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={isSaving}>
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div></>
    );
  }

  // ========== ADD MODE: Mode Selector → Manual Entry ==========

  // Screen 1: Mode Selection
  if (recipeMode === 'select') {
    return (
      <>{domainValidationModal}
      <div className="modal-overlay">
        <div className="modal-content modal-content-md">
          <div className="modal-header">
            <h3 className="modal-title">Add Recipe</h3>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="rf-modeSelector">
              <p className="rf-modePrompt">How would you like to add a recipe?</p>

              <div className="rf-modeCard" onClick={() => setRecipeMode('manual')}>
                <h4 className="rf-modeCardTitle"><span className="rf-modeIcon">&#9997;</span> Enter Manually</h4>
                <p className="rf-modeCardDesc">
                  Type your recipe details or upload a file (PDF, text, or photo of a recipe).
                  AI will extract text from uploaded files automatically.
                </p>
              </div>

              <div className="rf-modeCard" onClick={() => { if (onOpenRecipeGenerator) onOpenRecipeGenerator(); else onClose(); }}>
                <h4 className="rf-modeCardTitle"><span className="rf-modeIcon">&#129504;</span> AI Generate</h4>
                <p className="rf-modeCardDesc">
                  AI creates a personalized recipe based on your health profile, preferences,
                  dietary constraints, and pantry contents.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div></>
    );
  }

  // Screen 2: Manual Entry Form
  const isImageFile = selectedFile && selectedFile.type && selectedFile.type.startsWith('image/');
  const hasFormContent = formData.recipe_name.trim() && (formData.ingredients.trim() || formData.instructions.trim());

  return (
    <>{domainValidationModal}
    <div className="modal-overlay">
      <div className="modal-content modal-content-lg">
        <div className="modal-header">
          <h3 className="modal-title">Add Recipe</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <button type="button" className="rf-backLink" onClick={() => { setRecipeMode('select'); removeFile(); }}>
            &#8592; Back to options
          </button>

          {/* File Upload Zone */}
          {!selectedFile ? (
            <div
              className={`rf-uploadZone ${isDragging ? 'rf-uploadZone--dragging' : ''}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="rf-uploadIcon">&#128196;</div>
              <div className="rf-uploadTitle">Upload a recipe file (optional)</div>
              <div className="rf-uploadSubtitle">
                PDF, text, or photo of a recipe card/cookbook page &bull; Max 10MB<br/>
                AI will extract the recipe text automatically
              </div>
            </div>
          ) : (
            <div className="rf-uploadedFile">
              <div className="rf-uploadedFileInfo">
                <span className="rf-uploadedFileIcon">{isImageFile ? '\u{1F4F7}' : '\u{1F4C4}'}</span>
                <div>
                  <div className="rf-uploadedFileName">{selectedFile.name}</div>
                  <div className="rf-uploadedFileSize">
                    {(selectedFile.size / 1024).toFixed(1)} KB &bull; {isImageFile ? 'Image' : selectedFile.type === 'application/pdf' ? 'PDF' : 'Text'}
                    {extractionDone && useAsImage && isImageFile && ' \u2022 Will be used as recipe image'}
                  </div>
                </div>
              </div>
              <button type="button" className="rf-uploadedFileRemove" onClick={removeFile}>Remove</button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileInputChange}
            accept=".pdf,.jpg,.jpeg,.png,.txt"
            className="hidden"
          />

          {/* AI Extraction Banner & Button */}
          {selectedFile && !extractionDone && !isExtracting && (
            <>
              <div className="rf-extractionBanner">
                <span className="rf-ebIcon">&#129504;</span>
                <div className="rf-ebText">
                  <strong>AI will extract the recipe</strong> from this file and populate the form below.
                  You can review and edit before saving.
                </div>
              </div>

              {isImageFile && (
                <label className="rf-imageCheckboxRow">
                  <input type="checkbox" checked={useAsImage} onChange={(e) => setUseAsImage(e.target.checked)} />
                  <span>Use this photo as the recipe image (skip AI image generation)</span>
                </label>
              )}

              <button type="button" className="btn rf-extractBtn"
                      onClick={() => spend(extractApiType(), 'Extract the recipe from this file?', handleExtract)}>
                &#129504; Extract Recipe from File <CostChip apiType={extractApiType()} />
              </button>
            </>
          )}

          {/* Extraction Error */}
          {extractionError && (
            <div className="rf-extractionError">
              <p>{extractionError}</p>
              <button type="button" className="btn btn-secondary rf-retryBtn"
                      onClick={() => spend(extractApiType(), 'Extract the recipe from this file?', handleExtract)}>
                Retry Extraction
              </button>
            </div>
          )}

          {/* Extraction Spinner */}
          {isExtracting && (
            <div className="rf-extractionProgress">
              <div className="rf-extractionSpinner"></div>
              <p className="rf-extractionProgressText">AI is reading your recipe{isImageFile ? ' image' : ' file'}...</p>
              <p className="rf-extractionProgressSub">Extracting name, ingredients, instructions, and nutrition</p>
            </div>
          )}

          {/* Extraction Success Banner */}
          {extractionDone && (
            <div className="rf-extractionBanner rf-extractionBanner--success">
              <span className="rf-ebIcon">&#10003;</span>
              <div className="rf-ebText">
                <strong>Recipe extracted successfully.</strong> Review the details below and edit as needed before saving.
              </div>
            </div>
          )}

          <div className="rf-sectionDivider">{selectedFile ? (extractionDone ? 'extracted recipe' : 'form fields') : 'or type below'}</div>

          {/* Manual Form Fields */}
          <form onSubmit={handleSubmit}>
            <div className={`rf-formSection ${isExtracting ? 'rf-formSection--disabled' : ''}`}>
              <div className="form-group">
                <label className="form-label">Recipe Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.recipe_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipe_name: e.target.value }))}
                  placeholder="e.g., Grilled Salmon with Lemon Dill"
                  disabled={isExtracting}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of the dish"
                  disabled={isExtracting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Ingredients</label>
                <textarea
                  className="form-textarea"
                  rows="8"
                  value={formData.ingredients}
                  onChange={(e) => setFormData(prev => ({ ...prev, ingredients: e.target.value }))}
                  placeholder={'One ingredient per line\ne.g., 2 salmon fillets (6 oz each)\n1 tbsp olive oil\n1 lemon, juiced'}
                  disabled={isExtracting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Instructions</label>
                <textarea
                  className="form-textarea"
                  rows="8"
                  value={formData.instructions}
                  onChange={(e) => setFormData(prev => ({ ...prev, instructions: e.target.value }))}
                  placeholder="Step-by-step cooking instructions"
                  disabled={isExtracting}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Recipe Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.recipe_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, recipe_date: e.target.value }))}
                  disabled={isExtracting}
                />
              </div>

              <div className="rf-nutritionGrid">
                <div className="form-group">
                  <label className="form-label">Calories</label>
                  <input type="number" className="form-input" value={formData.calories}
                    onChange={(e) => setFormData(prev => ({ ...prev, calories: e.target.value }))} placeholder="kcal" disabled={isExtracting} />
                </div>
                <div className="form-group">
                  <label className="form-label">Protein (g)</label>
                  <input type="number" className="form-input" value={formData.protein}
                    onChange={(e) => setFormData(prev => ({ ...prev, protein: e.target.value }))} placeholder="g" disabled={isExtracting} />
                </div>
                <div className="form-group">
                  <label className="form-label">Carbs (g)</label>
                  <input type="number" className="form-input" value={formData.carbs}
                    onChange={(e) => setFormData(prev => ({ ...prev, carbs: e.target.value }))} placeholder="g" disabled={isExtracting} />
                </div>
                <div className="form-group">
                  <label className="form-label">Fat (g)</label>
                  <input type="number" className="form-input" value={formData.fat}
                    onChange={(e) => setFormData(prev => ({ ...prev, fat: e.target.value }))} placeholder="g" disabled={isExtracting} />
                </div>
              </div>

              <div className="rf-validationInfo">
                <span className="rf-viIcon">&#128274;</span>
                <span>All recipes are validated for food/nutrition relevance before saving to protect your health data.</span>
              </div>

              <div className="form-actions">
                <button type="button" onClick={onClose} className="btn btn-secondary" disabled={isSaving || isExtracting}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={isSaving || isExtracting || !formData.recipe_name.trim()}>
                  {isSaving ? 'Saving...' : 'Save Recipe'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div></>
  );
}

export default RecipeForm;
